import { useLeftNavigationTheme } from '@ben/orion/hooks/use-left-navigation-theme';
import MantineTooltip from '@ben/orion/mantine-tooltip';
import type { ITheme } from '@ben/orion/utils/theme';
import { Avatar, Image, ScrollArea } from '@mantine/core';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';

export interface LeftNavigationLayoutProps {
  children: ReactNode;
  employerLogo?: string;
  employerName?: string;
  employeeCard: ReactNode;
  employerCombobox?: ReactNode;
  /**
   * Optional sticky footer rendered below the scrollable nav links.
   * Stays pinned at the bottom of the nav while children scroll.
   */
  footer?: ReactNode;
  showBenGlow?: boolean;
  // only used for storybook
  bgColor?: string;
  dividerPadding?: string;
}
/**
 * Layout component that renders a header containing brand and employer logos, and an employeeCard, followed by a divider and navigation links which are it's children.
 * This must be used in a nav or AppShell.Nav component.
 */
const LeftNavigationLayout = ({
  children,
  employerLogo,
  employerName,
  employeeCard,
  employerCombobox,
  footer,
  bgColor,
  showBenGlow = false,
  dividerPadding,
}: LeftNavigationLayoutProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { image, isDarkBgColorVariant } = useLeftNavigationTheme();

  const dividerBg = useMemo(() => {
    if (showBenGlow) {
      return `url(${process.env.REACT_APP_STATIC_URL}/images/ben-glow-gradient.png)`;
    }
    return isDarkBgColorVariant ? theme.palette.white(0.15) : '#D9D9D9';
  }, [isDarkBgColorVariant, showBenGlow, theme.palette]);

  return (
    <Container $bgColor={bgColor}>
      <Header>
        <Logos>
          <Link to="/" aria-label={t('leftNavigation.home')}>
            <Image
              src={
                image ??
                `${process.env.REACT_APP_STATIC_URL}/images/ben-header-logo-mono.svg`
              }
              alt={t('leftNavigation.brandLogo')}
              h={28}
              w="auto"
              maw={170}
              fit="contain"
            />
          </Link>
          {employerLogo && (
            <MantineTooltip label={employerName} disabled={!employerName}>
              <Avatar
                src={employerLogo}
                alt={`${employerName ?? t('leftNavigation.employer')} ${t('leftNavigation.logo')}`}
                radius={4}
                size={28}
              />
            </MantineTooltip>
          )}
        </Logos>
        <Menus>
          {employeeCard}
          {employerCombobox}
        </Menus>
      </Header>
      <Divider $bgColor={dividerBg} $padding={dividerPadding} />
      <StyledScrollArea scrollbars="y" $isDarkBgVariant={isDarkBgColorVariant}>
        {children}
      </StyledScrollArea>
      {footer != null && <Footer>{footer}</Footer>}
    </Container>
  );
};

export default LeftNavigationLayout;

const Container = styled.div<{
  $bgColor?: string;
}>`
  ${({ theme, $bgColor }) => {
    return css`
      padding: ${theme.spacing(2.5, 0)};
      background-color: ${$bgColor ?? theme.components.leftNavigation.bgColor};
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
    `;
  }}
`;

const Header = styled.div`
  ${({ theme }: { theme: ITheme }) => css`
    padding: ${theme.spacing(0, 2)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2.5)};
  `}
`;

const Logos = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
`;

const Divider = styled.div<{ $bgColor: string; $padding?: string }>`
  ${({ theme, $bgColor, $padding }) => css`
    background: ${$bgColor};
    height: 1px;
    min-height: 1px;
    margin: ${$padding ?? theme.spacing(2.5, 0, 3, 0)};
  `}
`;

const StyledScrollArea = styled(ScrollArea)<{ $isDarkBgVariant?: boolean }>`
  ${({ theme, $isDarkBgVariant }) => css`
    flex: 1;
    min-height: 0;
    padding: ${theme.spacing(0, 2)};
    .mantine-ScrollArea-thumb {
      background-color: ${$isDarkBgVariant
        ? theme.palette.white(0.25)
        : theme.palette.black(0.15)};
    }
    // override styling because mantine's ScrollArea has an anonymous div with display: table; inside the viewport which causes the right section to overflow:
    .mantine-ScrollArea-viewport > div {
      display: flex !important;
      flex-direction: column;
      height: 100%;
    }
  `}
`;

const Footer = styled.div`
  ${({ theme }) => css`
    flex-shrink: 0;
    padding: ${theme.spacing(2, 2, 0, 2)};
  `}
`;

const Menus = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;
